import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, Beaker, UserCheck, CalendarClock,
  ListChecks, StickyNote, Menu, X, Factory, GitBranch,
  Package, ShoppingCart, Boxes, ShieldCheck, LogOut, Download, Trash2,
  Wheat, AlertOctagon, FlaskConical, Sparkles, FileText,
} from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { downloadBackup } from "@/lib/api";
import { toast } from "sonner";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/leads", label: "Leads", icon: Users, testid: "nav-leads" },
  { to: "/customers", label: "Customers", icon: UserCheck, testid: "nav-customers" },
  { to: "/orders", label: "Orders", icon: ShoppingCart, testid: "nav-orders" },
  { to: "/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices" },
  { to: "/inventory", label: "Inventory", icon: Boxes, testid: "nav-inventory" },
  // ---- Production ----
  { section: "Production", icon: Factory },
  { to: "/production", label: "Batches", icon: Factory, testid: "nav-production" },
  { to: "/waste", label: "Waste", icon: Trash2, testid: "nav-waste" },
  // ---- Omavalvonta (Food Safety) — single entry ----
  { to: "/omavalvonta", label: "Omavalvonta", icon: Sparkles, testid: "nav-omavalvonta" },
  // ---- Traceability + other ----
  { to: "/traceability", label: "Traceability", icon: GitBranch, testid: "nav-traceability" },
  { to: "/followups", label: "Follow Ups", icon: CalendarClock, testid: "nav-followups" },
  { to: "/tasks", label: "Tasks", icon: ListChecks, testid: "nav-tasks" },
  { to: "/notes", label: "Notes", icon: StickyNote, testid: "nav-notes" },
];

const Brand = ({ size = "md" }) => (
  <div className="flex items-center gap-3" data-testid="brand-logo">
    <img
      src="/garnacha-logo.svg"
      alt="Garnacha"
      className={size === "lg" ? "w-12 h-12 object-contain" : "w-10 h-10 object-contain"}
      draggable={false}
    />
    <div className="leading-tight">
      <div className="font-display font-bold text-[17px] text-[#222] tracking-tight">GARNACHA</div>
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#1F4D3A] font-semibold">
        System
      </div>
    </div>
  </div>
);

const SidebarLinks = ({ onClick }) => (
  <nav className="flex flex-col gap-1">
    {NAV.map((item, idx) => {
      if (item.section) {
        const SectionIcon = item.icon;
        return (
          <div key={`section-${idx}`} className="flex items-center gap-2 px-3.5 pt-4 pb-1 text-[10px] uppercase tracking-[0.18em] text-[#1F4D3A] font-bold">
            {SectionIcon && <SectionIcon size={12} />}
            {item.section}
          </div>
        );
      }
      const { to, label, icon: Icon, end, testid } = item;
      return (
        <NavLink
          key={to}
          to={to}
          end={end}
          onClick={onClick}
          data-testid={testid}
          className={({ isActive }) =>
            [
              "flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors",
              isActive
                ? "bg-[#FDEBDD] text-[#E85D04]"
                : "text-[#222] hover:bg-[#F7F1E8] hover:text-[#E85D04]",
            ].join(" ")
          }
        >
          <Icon size={18} strokeWidth={2} />
          <span>{label}</span>
        </NavLink>
      );
    })}
  </nav>
);

export default function Layout() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const current = NAV.find((n) => (n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to)));

  const handleBackup = () => {
    downloadBackup();
    toast.success("Downloading full CRM backup (.zip)");
  };

  return (
    <div className="min-h-screen bg-[#F7F1E8] flex">
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:flex flex-col w-64 bg-white border-r border-[#E5E5E5] sticky top-0 h-screen px-4 py-6 gap-6"
        data-testid="sidebar"
      >
        <Brand />
        <SidebarLinks />
        {isAdmin && (
          <div className="border-t border-[#E5E5E5] pt-3">
            <div className="brand-overline mb-2">Admin</div>
            <NavLink
              to="/admin/users"
              data-testid="nav-admin-users"
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isActive
                    ? "bg-[#FDEBDD] text-[#E85D04]"
                    : "text-[#222] hover:bg-[#F7F1E8] hover:text-[#E85D04]",
                ].join(" ")
              }
            >
              <ShieldCheck size={18} strokeWidth={2} />
              <span>Authorized Users</span>
            </NavLink>
            <button
              onClick={handleBackup}
              data-testid="download-backup-btn"
              className="w-full mt-1 flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium text-[#222] hover:bg-[#F7F1E8] hover:text-[#E85D04] transition-colors"
            >
              <Download size={18} strokeWidth={2} />
              <span>Download Backup</span>
            </button>
          </div>
        )}
        {user && (
          <div className="mt-auto border-t border-[#E5E5E5] pt-3 flex flex-col gap-2" data-testid="user-menu">
            <div className="flex items-center gap-2 px-1">
              {user.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#E85D04] text-white flex items-center justify-center font-bold text-sm">
                  {(user.name || user.email).slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="min-w-0">
                <div className="text-[12px] font-semibold text-[#222] truncate">{user.name || user.email}</div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-[#E85D04]">{user.role}</div>
              </div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#6C757D] hover:text-[#E76F51] hover:bg-[#E76F51]/10 transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#1F4D3A] font-semibold mt-1">
              DRIP IT. DIP IT. <span className="text-[#E85D04]">LOVE IT.™</span>
            </div>
          </div>
        )}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div className="bg-black/30 absolute inset-0" onClick={() => setOpen(false)} />
          <aside className="relative bg-white w-72 h-full px-4 py-6 flex flex-col gap-6 shadow-xl">
            <div className="flex items-center justify-between">
              <Brand />
              <button
                onClick={() => setOpen(false)}
                className="p-2 rounded-lg hover:bg-[#F7F1E8]"
                data-testid="close-mobile-drawer"
              >
                <X size={20} />
              </button>
            </div>
            <SidebarLinks onClick={() => setOpen(false)} />
            {isAdmin && (
              <div className="border-t border-[#E5E5E5] pt-3">
                <div className="brand-overline mb-2">Admin</div>
                <NavLink
                  to="/admin/users"
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    [
                      "flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium transition-colors",
                      isActive ? "bg-[#FDEBDD] text-[#E85D04]" : "text-[#222] hover:bg-[#F7F1E8]",
                    ].join(" ")
                  }
                >
                  <ShieldCheck size={18} /> <span>Authorized Users</span>
                </NavLink>
                <button
                  onClick={() => { setOpen(false); handleBackup(); }}
                  className="w-full mt-1 flex items-center gap-3 px-3.5 py-2.5 rounded-lg text-sm font-medium text-[#222] hover:bg-[#F7F1E8]"
                >
                  <Download size={18} /> <span>Download Backup</span>
                </button>
              </div>
            )}
            {user && (
              <div className="mt-auto border-t border-[#E5E5E5] pt-3 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#E85D04] text-white flex items-center justify-center font-bold text-sm">
                      {(user.name || user.email).slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-xs font-semibold text-[#222] truncate">{user.name || user.email}</div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-[#E85D04]">{user.role}</div>
                  </div>
                </div>
                <button onClick={logout} className="p-2 rounded-lg text-[#6C757D] hover:text-[#E76F51] hover:bg-[#E76F51]/10">
                  <LogOut size={16} />
                </button>
              </div>
            )}
          </aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header
          className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-[#E5E5E5]"
          data-testid="topbar"
        >
          <div className="px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                className="lg:hidden p-2 rounded-lg hover:bg-[#F7F1E8]"
                onClick={() => setOpen(true)}
                data-testid="open-mobile-drawer"
              >
                <Menu size={22} />
              </button>
              <div className="lg:hidden"><Brand /></div>
              <div className="hidden lg:block">
                <div className="text-[11px] tracking-[0.22em] uppercase text-[#E85D04] font-bold">
                  {current?.label || "Dashboard"}
                </div>
                <div className="font-display font-semibold text-lg text-[#222] -mt-0.5">
                  {current?.label === "Dashboard" ? "Welcome back" : current?.label}
                </div>
              </div>
            </div>
            <div className="hidden sm:block text-right">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#1F4D3A] font-semibold">
                Goal · Recurring
              </div>
              <div className="font-display font-bold text-[#E85D04] text-base">€10,000 / month</div>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-6 pb-24 lg:pb-10 max-w-7xl w-full mx-auto">
          <Outlet />
        </main>

        {/* Mobile bottom nav */}
        <nav
          className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-[#E5E5E5] grid grid-cols-5"
          data-testid="bottom-nav"
        >
          {NAV.slice(0, 5).map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`bottom-${testid}`}
              className={({ isActive }) =>
                [
                  "flex flex-col items-center justify-center py-2 text-[10px] gap-0.5",
                  isActive ? "text-[#E85D04]" : "text-[#6C757D]",
                ].join(" ")
              }
            >
              <Icon size={20} />
              <span className="truncate max-w-[64px]">{label.split(" ")[0]}</span>
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
